import React from 'react';
import { ExternalLink, ArrowRight, LayoutDashboard, GitBranch, ClipboardList,
  BarChart3, FolderGit2, Users2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';

const SECTORS = [
  'IT & Digital', 'Pharmaceutical', 'Manufacturing', 'Energy', 'Utilities',
  'Property', 'Infrastructure', 'Education', 'Professional Services',
  'Construction', 'Healthcare', 'Other',
];

const PM_DASHBOARDS = [
  { icon: LayoutDashboard, name: 'Portfolio dashboard',
    body: 'Every project in your sector environment, rolled up by RAG, stage, owner and priority.' },
  { icon: BarChart3, name: 'Executive rollup',
    body: 'Board-ready narrative report, plain-English summary composed from live delivery data.' },
  { icon: FolderGit2, name: 'Project workspace',
    body: 'Structured stage-gated workspace with governance, evidence and role-based approvals.' },
  { icon: GitBranch, name: 'Programme view',
    body: 'Cross-project dependencies, milestones and shared resources across the portfolio.' },
  { icon: ClipboardList, name: 'Requirements &amp; scope',
    body: 'From intake through prioritisation to project, traceable back to the business need.' },
  { icon: Users2, name: 'Team &amp; RACI',
    body: 'Role-based access and RACI, mapped to your organisation&rsquo;s structure not ours.' },
];

const V2VideraPM = () => {
  useSEO({
    title: 'Videra PM | Configurable project delivery platform',
    description: 'Choose your sector and create a structured project delivery environment around the way you work, configurable Videra for IT, pharma, manufacturing, energy and more.',
    path: '/videra/pm',
  });

  return (
    <div data-testid="v2-videra-pm">
      <ProductHero
        eyebrow="Videra PM"
        title="Your sector. Your projects. Your Videra."
        subtitle="Create a project delivery platform around the way you work."
        body={(
          <>
            <p>Generic project-management tools often start with a blank workspace and expect organisations to adapt. Videra PM takes a different approach.</p>
            <p>Choose your sector and create a structured delivery environment around the type of projects your organisation manages.</p>
          </>
        )}
        primary={{ label: 'Create My Videra', href: 'https://pm.videra.app' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        supportingLine="Available to try with full functionality."
        heroImage="/images/construction/cp-portfolio-pmo.jpg"
        heroImageAlt="Videra PM configurable portfolio environment"
        testid="videra-pm-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Sectors</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 max-w-3xl">Pick a sector. Get a delivery environment shaped around it.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {SECTORS.map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800" data-testid={`pm-sector-${s.toLowerCase().replace(/\W+/g,'-')}`}>
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">How it works</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">Six steps from sector choice to portfolio.</h2>
          <Workflow testid="pm-workflow" steps={['Choose your sector','Create your environment','Define delivery','Add projects','Manage delivery','See the portfolio']} />
        </div>
      </section>

      {/* Dashboards you get inside a Videra PM environment */}
      <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="pm-dashboards">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Inside a Videra PM environment</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 max-w-3xl">Dashboards you get from day one.</h2>
          <p className="mt-4 text-slate-600 max-w-3xl leading-relaxed">
            Every Videra PM environment is generated with a consistent set of structured dashboards and workspaces,
            tuned to your sector but architected from the same operating philosophy.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PM_DASHBOARDS.map((d) => (
              <div key={d.name} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`pm-dashboard-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-3">
                  <d.icon className="w-4 h-4" />
                </div>
                <p className="text-lg font-medium text-slate-900" dangerouslySetInnerHTML={{ __html: d.name }} />
                <p className="mt-2 text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: d.body }} />
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <a
              href="https://pm.videra.app"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group"
              data-testid="pm-dashboards-cta"
            >
              Create My Videra <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a href="https://pm.videra.app" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-600" data-testid="pm-dashboards-url">
              pm.videra.app <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight max-w-3xl">
            Your organisation shouldn&rsquo;t have to fit the software.
          </h2>
          <div className="mt-8 text-base lg:text-lg text-slate-300 leading-relaxed space-y-4">
            <p>Create the environment around your projects, governance and delivery model.</p>
            <p>Where lifecycle is relevant to the selected industry, connect lifecycle requirements and project interventions within the same environment.</p>
          </div>
          <div className="mt-10">
            <a href="https://pm.videra.app" className="inline-flex items-center gap-2 bg-white text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors" data-testid="pm-create-cta">
              Create My Videra →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default V2VideraPM;
